import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOptionalSession } from 'src/contexts/SessionContext';
import i18n from 'src/i18n';
import { NotificationSetting } from 'src/models/user/_types';
import {
  fetchNotificationSettings,
  updateNotificationSettings,
} from 'src/models/user/user';

const i18nReplace = (text: string) => {
  return text
    .replace('Group Membership', i18n.t('group.membership.one'))
    .replace('Group', i18n.t('group.one'))
    .replace('group', i18n.t('group.one').toLowerCase())
    .replace('Workout Plan', i18n.t('workout-plan.other'))
    .replace('workout plan', i18n.t('workout-plan.one').toLowerCase())
    .replace('clients', i18n.t('trainer.client.other').toLowerCase())
    .replace('Client', i18n.t('trainer.client.one'))
    .replace('client', i18n.t('trainer.client.one').toLowerCase());
};

export type UseNotificationOptions = {
  hash?: string | null;
  /** When using hash-only auth, mirrors logged-in promotional_email row hiding. */
  hidePromotionalEmail?: boolean;
};

export const useNotification = (options?: UseNotificationOptions) => {
  const { sessionTrainer } = useOptionalSession();
  const hash = options?.hash ?? undefined;
  const hidePromotionalEmail =
    options?.hash != null && options.hash !== ''
      ? (options.hidePromotionalEmail ?? false)
      : !!sessionTrainer;

  const queryClient = useQueryClient();

  const { data: notificationSettings = [], isLoading } = useQuery({
    queryKey: ['notificationSettings', hash ?? 'session'],
    queryFn: () =>
      fetchNotificationSettings({ webSettings: true, hash: hash ?? undefined }),
  });

  const updateNotificationSettingsMutation = useMutation({
    mutationFn: (settings: NotificationSetting[]) =>
      updateNotificationSettings(settings, { hash: hash ?? undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['notificationSettings', hash ?? 'session'],
      });
    },
  });

  let settings = notificationSettings;
  if (hidePromotionalEmail) {
    settings = settings.filter((item) => item.id !== 'promotional_email');
  }

  const notifications = settings.map((item) => ({
    ...item,
    title: i18nReplace(item.title || ''),
    description: i18nReplace(item.description || ''),
    platformDescription: i18nReplace(item.platformDescription || ''),
    trainerDescription: i18nReplace(item.trainerDescription || ''),
  }));

  return {
    notifications,
    isLoading,
    updateNotificationSettingsMutation,
  };
};
